/**
 * Inline translations for the auth-origin app.
 *
 * Deliberately not using @updatecore/i18n — the auth-origin is meant to
 * stay tiny (target bundle <60KB gzipped per spec §8) and the visible
 * surface is just a handful of short strings. Adding the full backend-fed
 * translation table would balloon the bundle.
 *
 * Keys cover the four routes plus shared status messages. Adding a new
 * key needs a translation in all four locales to satisfy the type.
 */

export const SUPPORTED_LOCALES = ['de', 'en', 'fr', 'it'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

type TranslationKeys = {
  app_title: string;
  use_passkey: string;
  register_passkey: string;
  waiting: string;
  cancel: string;
  success: string;
  error_unknown: string;
  error_timeout: string;
  error_user_cancelled: string;
  error_no_support: string;
  register_label: string;
  username_label: string;
  close_window: string;
};

const TRANSLATIONS: Record<SupportedLocale, TranslationKeys> = {
  de: {
    app_title: 'Bei update Fitness anmelden',
    use_passkey: 'Mit Passkey anmelden',
    register_passkey: 'Passkey registrieren',
    waiting: 'Warten auf deinen Authenticator…',
    cancel: 'Abbrechen',
    success: 'Erfolgreich.',
    error_unknown: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    error_timeout: 'Passkey hat zu lange gedauert. Bitte erneut versuchen.',
    error_user_cancelled: 'Abgebrochen.',
    error_no_support: 'Dein Browser unterstützt Passkeys (noch) nicht.',
    register_label: 'Name für diesen Passkey',
    username_label: 'Benutzername',
    close_window: 'Fenster schliessen',
  },
  en: {
    app_title: 'Sign in to update Fitness',
    use_passkey: 'Use your passkey',
    register_passkey: 'Register a passkey',
    waiting: 'Waiting for your authenticator…',
    cancel: 'Cancel',
    success: 'Done.',
    error_unknown: 'Something went wrong. Please try again.',
    error_timeout: 'Passkey took too long. Try again.',
    error_user_cancelled: 'Cancelled.',
    error_no_support: 'This browser does not support passkeys yet.',
    register_label: 'Name for this passkey',
    username_label: 'Username',
    close_window: 'Close window',
  },
  fr: {
    app_title: 'Se connecter à update Fitness',
    use_passkey: 'Utiliser votre clé d’accès',
    register_passkey: 'Enregistrer une clé d’accès',
    waiting: 'En attente de votre authentificateur…',
    cancel: 'Annuler',
    success: 'Terminé.',
    error_unknown: 'Une erreur est survenue. Veuillez réessayer.',
    error_timeout: 'La clé d’accès a pris trop de temps. Veuillez réessayer.',
    error_user_cancelled: 'Annulé.',
    error_no_support: 'Ce navigateur ne prend pas encore en charge les clés d’accès.',
    register_label: 'Nom pour cette clé d’accès',
    username_label: 'Nom d’utilisateur',
    close_window: 'Fermer la fenêtre',
  },
  it: {
    app_title: 'Accedi a update Fitness',
    use_passkey: 'Usa la tua passkey',
    register_passkey: 'Registra una passkey',
    waiting: 'In attesa del tuo authenticator…',
    cancel: 'Annulla',
    success: 'Fatto.',
    error_unknown: 'Qualcosa è andato storto. Riprova.',
    error_timeout: 'La passkey ha impiegato troppo tempo. Riprova.',
    error_user_cancelled: 'Annullato.',
    error_no_support: 'Questo browser non supporta ancora le passkey.',
    register_label: 'Nome per questa passkey',
    username_label: 'Nome utente',
    close_window: 'Chiudi finestra',
  },
};

/**
 * Resolves the active locale from navigator.language. Falls back to 'de'
 * (the primary update Fitness language) when the navigator language is
 * not one of the supported four.
 */
export function pick_locale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'de';
  const lang = (navigator.language ?? '').slice(0, 2).toLowerCase();
  const supported = SUPPORTED_LOCALES as readonly string[];
  return supported.includes(lang) ? (lang as SupportedLocale) : 'de';
}

/**
 * Returns a typed translation lookup for the given locale. Callers
 * destructure or read keys directly — keeps usage terse at call sites
 * compared to a `t('key.path')` style hook.
 */
export function get_translations(locale?: SupportedLocale): TranslationKeys {
  return TRANSLATIONS[locale ?? pick_locale()];
}
